import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Package, Clock, CheckCircle, Truck, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped: 'bg-orange-100 text-orange-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const PAYMENT_STYLES = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  failed: 'bg-red-100 text-red-700',
};

const OrderHistoryPage = () => {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError('');
    setOrders(null);

    try {
      const res = await axios.post(`${API}/orders/history`, { email: email.trim() });
      setOrders(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Unable to find orders for this email.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Order History | The Perfect Peach Club</title>
      </Helmet>
      <Navigation />
      <ChatWidget />

      <div className="max-w-3xl mx-auto px-5 md:px-6 pt-24 md:pt-32 pb-12 md:pb-20">
        <div className="text-center mb-8">
          <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-[#2D2420] mb-3" data-testid="order-history-heading">
            Order History
          </h1>
          <p className="text-african-earth text-base md:text-lg max-w-lg mx-auto">
            Enter your email to view all your past orders
          </p>
        </div>

        <form onSubmit={handleLookup} className="bg-white rounded-3xl p-6 sm:p-8 shadow-soft mb-8" data-testid="order-history-form">
          <label className="block text-african-earth font-medium mb-2 text-sm">Email Address</label>
          <div className="flex gap-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
              className="flex-1 border border-[#E0D5D0] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-perfect-peach text-sm"
              data-testid="order-history-email-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-perfect-peach text-white px-6 py-3 rounded-full font-medium hover:bg-[#C57D63] transition-all duration-300 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              data-testid="order-history-submit"
            >
              <Mail className="w-4 h-4" />
              {loading ? 'Searching...' : 'Look Up'}
            </button>
          </div>
        </form>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6 flex items-start gap-3" data-testid="order-history-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {orders && (
          <div className="space-y-4" data-testid="order-history-results">
            <p className="text-african-earth text-sm">{orders.length} order{orders.length !== 1 ? 's' : ''} found</p>
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl p-5 sm:p-6 shadow-soft">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm font-medium text-[#2D2420]">#{order.id.slice(0, 8)}</p>
                    <p className="text-xs text-african-earth mt-0.5">{formatDate(order.created_at)}</p>
                  </div>
                  <div className="flex gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${STATUS_STYLES[order.status] || 'bg-gray-100 text-gray-700'}`}>
                      {order.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${PAYMENT_STYLES[order.payment_status] || 'bg-gray-100 text-gray-700'}`}>
                      {order.payment_status}
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#F0EAE6] pt-3 space-y-1.5">
                  {order.items?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-african-earth">{item.quantity}x Product</span>
                      <span className="text-[#2D2420] font-medium">R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm text-african-earth">
                    <span>Shipping ({order.shipping_option === 'local' ? 'Local' : 'Nationwide'})</span>
                    <span>{order.shipping_cost === 0 ? 'FREE' : `R${order.shipping_cost?.toFixed(2)}`}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-[#E0D5D0]">
                  <span className="font-bold text-[#2D2420]">Total</span>
                  <span className="font-bold text-perfect-peach text-lg">R{order.total_amount?.toFixed(2)}</span>
                </div>

                <div className="mt-3">
                  <Link
                    to={`/track-order`}
                    className="text-perfect-peach text-sm font-medium hover:underline"
                  >
                    Track this order
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {!orders && !error && (
          <p className="text-center text-african-earth/60 text-sm">Your order confirmation email contains the email address linked to your orders.</p>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default OrderHistoryPage;
